import PropTypes from 'prop-types';
import {QUALITY_ISSUES_SORTED_STATUSES} from '../status-types';
import {SUPPORTED_ISSUE_MODEL_TYPES} from '../issue-types';
import {
  TEMPLATE_CATEGORY_FIELD_NAMES,
  TEMPLATE_CATEGORY_PERMITTED_ACTIONS,
  TEMPLATE_FIELD_NAMES,
  TEMPLATE_PERMITTED_ACTIONS
} from '../issue-templates';
import {PAGE_PARAMS, SORTING_PARAMS, TEMPLATES_FILTER_PROPERTIES} from '../issue-query-params';
import {HQ_COMPANY_TYPE, HQ_ROLE_TYPE, HQ_USER_TYPE} from '../hq-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../issue-attribute-names';

export const documentVersionType = PropTypes.number;
export const issueModelType = PropTypes.oneOf(SUPPORTED_ISSUE_MODEL_TYPES);

export const statusType = PropTypes.oneOf(QUALITY_ISSUES_SORTED_STATUSES);

export const historyAttributesType = PropTypes.object;

export const storeAttributesType = PropTypes.object;

export const fieldIssueTypesType = PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string]));

export const idToTitleMapType = PropTypes.objectOf(PropTypes.string);

export const locationsNodeType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  order: PropTypes.number
});
export const locationsTreeNodesType = PropTypes.arrayOf(locationsNodeType);

export const locationsTreeNodesDictionaryType = PropTypes.objectOf(locationsNodeType);

export const reactRouterParamsType = PropTypes.shape({
  splat: PropTypes.string
});

export const queryParamsType = PropTypes.shape({
  sort: PropTypes.string.isRequired,
  statusFilter: PropTypes.arrayOf(PropTypes.string),
  issueTypeFilter: PropTypes.arrayOf(PropTypes.string),
  assignedToFilter: PropTypes.arrayOf(PropTypes.string),
  createdAtStartFilter: PropTypes.string,
  createdAtEndFilter: PropTypes.string,
  locationFilter: PropTypes.string,
  rootCauseFilter: PropTypes.arrayOf(PropTypes.string)
});

export const templatesQueryParamsType = PropTypes.shape({
  [SORTING_PARAMS.SORT]: PropTypes.string.isRequired,
  [PAGE_PARAMS.OFFSET]: PropTypes.number.isRequired,
  [TEMPLATES_FILTER_PROPERTIES.SEARCH]: PropTypes.string
});

export const locationAttributesType = PropTypes.object;

const coreIssueAttributesType = {
  dueDate: PropTypes.string,
  locationDetails: PropTypes.string,
  status: statusType.isRequired,
  title: PropTypes.string.isRequired
};

export const pendingIssueAttributesType = PropTypes.shape({
  ...coreIssueAttributesType,
  assignedTo: PropTypes.string.isRequired,
  assignedToType: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
});

export const pendingIssueFormAttributesType = PropTypes.object;

export const issueAttributesType = PropTypes.shape({
  ...coreIssueAttributesType,
  officialResponse: PropTypes.shape({
    response: PropTypes.string,
    respondedAt: PropTypes.string,
    respondedBy: PropTypes.string
  }),
  attachmentCount: PropTypes.number.isRequired,
  commentCount: PropTypes.number.isRequired,
  [ISSUE_ATTRIBUTE_NAMES.ID]: PropTypes.number.isRequired,
  permittedAttributes: PropTypes.array.isRequired,
  permittedStatuses: PropTypes.array.isRequired,
  permittedActions: PropTypes.array
});

export const commentAttributesType = PropTypes.shape({
  body: PropTypes.string.isRequired,
  createdBy: PropTypes.string.isRequired,
  issueId: PropTypes.string
});

//const changesetAttributes = ['status', 'answer', 'dueDate', 'assignedTo', 'assignedToType'];
const changesetAttributesType = PropTypes.shape({
  status: PropTypes.arrayOf(statusType),
  answer: PropTypes.arrayOf(PropTypes.string),
  dueDate: PropTypes.arrayOf(PropTypes.string),
  assignedTo: PropTypes.arrayOf(PropTypes.string),
  assignedToType: PropTypes.arrayOf(PropTypes.string)
});

export const changesetType = PropTypes.shape({
  createdAt: PropTypes.string.isRequired,
  createdBy: PropTypes.string,
  event: PropTypes.oneOf(['create', 'update']),
  changes: changesetAttributesType.isRequired,
  changesWithOxygen: changesetAttributesType.isRequired
});

export const templateCategoryType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  createdBy: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  permittedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(TEMPLATE_CATEGORY_PERMITTED_ACTIONS))).isRequired,
  permittedAttributes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(TEMPLATE_CATEGORY_FIELD_NAMES))).isRequired
});

export const templateCategoriesEntitiesMapType = PropTypes.objectOf(templateCategoryType);

export const templateType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  issueTemplateCategoryId: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  createdAt: PropTypes.string.isRequired,
  createdBy: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
  updatedBy: PropTypes.string.isRequired,
  permittedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(TEMPLATE_PERMITTED_ACTIONS))).isRequired,
  permittedAttributes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(TEMPLATE_FIELD_NAMES))).isRequired,
  issueAttributes: {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    typeId: PropTypes.string.isRequired,
    subtypeId: PropTypes.string.isRequired,
    rootCauseId: PropTypes.string.isRequired,
    assigneeId: PropTypes.string.isRequired,
    daysTilDueDate: PropTypes.number.isRequired,
    locationId: PropTypes.string.isRequired,
    locationDetails: PropTypes.string.isRequired,
    customAttributes: PropTypes.arrayOf(
      PropTypes.shape({
        customAttributesId: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired
  }
});

export const templatesCollectionType = PropTypes.arrayOf(templateType);

export const channelItemsType = PropTypes.array;
export const issuesSortOrderArrayType = PropTypes.arrayOf(PropTypes.string);

const channelItemWrapperArrayType = PropTypes.shape({
  error: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  sortOrder: issuesSortOrderArrayType.isRequired,
  collection: PropTypes.object.isRequired
});

export const issueWrapperType = PropTypes.shape({
  error: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  updating: PropTypes.bool.isRequired,
  channelItems: channelItemWrapperArrayType,
  attributes: issueAttributesType
});

const objectWithName = PropTypes.shape({
  name: PropTypes.string.isRequired
});

export const companiesType = PropTypes.objectOf(objectWithName);

export const oxygenType = PropTypes.shape({
  users: PropTypes.objectOf(objectWithName).isRequired,
  roles: PropTypes.objectOf(objectWithName).isRequired,
  companies: companiesType.isRequired
});

export const documentType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  version: documentVersionType.isRequired,
  latestVersion: documentVersionType,
  is2d: PropTypes.bool,
  fileName: PropTypes.string
});

export const optionalDocumentType = PropTypes.shape({
  name: PropTypes.string,
  version: documentVersionType,
  latestVersion: documentVersionType,
  is2d: PropTypes.bool,
  fileName: PropTypes.string
});

export const documentCollectionType = PropTypes.objectOf(optionalDocumentType);

export const issuesFetchParamsType = PropTypes.shape({
  offset: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  targetUrn: PropTypes.string,
  documentVersion: documentVersionType,
  queryParams: queryParamsType.isRequired
});

export const issueWrapperArrayType = PropTypes.arrayOf(issueWrapperType);

export const sheetObjectType = PropTypes.shape({
  sheetGuid: PropTypes.string.isRequired,
  sheetName: PropTypes.string.isRequired,
  viewableID: PropTypes.string.isRequired
});

export const statusOptionType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  colorClassName: PropTypes.string.isRequired,
  description: PropTypes.string
});

export const sortPropType = PropTypes.shape({
  sortField: PropTypes.string,
  isAscending: PropTypes.bool
});

export const statusOptionsArrayType = PropTypes.arrayOf(statusOptionType);

export const roleType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
});

export const companyType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
});

export const userCompanyType = PropTypes.shape({
  id: PropTypes.string,
  oxygenId: PropTypes.string,
  name: PropTypes.string
});

export const oxygenUserType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  oxygenId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  projectStatus: PropTypes.string.isRequired,
  profileImg50: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  isProjectAdmin: PropTypes.bool.isRequired,

  company: userCompanyType.isRequired,
  roles: PropTypes.arrayOf(roleType).isRequired
});

export const pushpinExtensionType = PropTypes.shape({
  updatePattern: PropTypes.func.isRequired,
  createItem: PropTypes.func.isRequired,
  setVisibleById: PropTypes.func.isRequired,
  selectOne: PropTypes.func.isRequired,
  updateItemById: PropTypes.func.isRequired,
  showAll: PropTypes.func.isRequired,
  setDraggableById: PropTypes.func.isRequired,
  addEventListener: PropTypes.func.isRequired,
  removeEventListener: PropTypes.func.isRequired,
  selectNone: PropTypes.func.isRequired,
  removeItemById: PropTypes.func.isRequired,
  startCreateItem: PropTypes.func.isRequired,
  endCreateItem: PropTypes.func.isRequired,
  pushPinManager: PropTypes.shape({
    getItemById: PropTypes.func.isRequired,
    getSelectedItem: PropTypes.func.isRequired,
    items: PropTypes.func.isRequired
  })
});

export const documentForCreateType = PropTypes.shape({
  /**
   * The name of the document
   */
  name: PropTypes.string.isRequired,
  /**
   * The full file name with extension.
   */
  fileName: PropTypes.string.isRequired,
  /**
   * Whether the document is 2d, used for analytics.
   */
  is2d: PropTypes.bool.isRequired,
  /**
   * The version of the document currently being viewed.
   */
  version: PropTypes.number.isRequired,
  /**
   * The document's most recent version, When the `latestVersion` is different from the `version` attribute it wont let you create new Issue.
   */
  latestVersion: PropTypes.number.isRequired
});

export const initialFieldValuesType = PropTypes.shape({
  title: PropTypes.string,
  lbsLocationId: PropTypes.string,
  assignedToId: PropTypes.string,
  assignedToType: PropTypes.oneOf([HQ_USER_TYPE, HQ_COMPANY_TYPE, HQ_ROLE_TYPE]),
  ownerId: PropTypes.string,
  description: PropTypes.string,
  rootCauseId: PropTypes.string,
  subtypeId: PropTypes.string
});
